import { Injectable, Inject, DOCUMENT } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

export interface Language {
  code: string;
  name: string;
  flag: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  // Langues supportées avec drapeaux
  readonly languages: Language[] = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' }
  ];

  private currentLanguageSubject = new BehaviorSubject<string>('en');
  currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor(
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.initLanguages();
  }

  private initLanguages(): void {
    const codes = this.languages.map(l => l.code);
    this.translate.addLangs(codes);
    this.translate.setDefaultLang('en');

    // Récupérer la langue sauvegardée ou détecter celle du navigateur
    let savedLang = localStorage.getItem('preferredLanguage');
    
    // Migration des anciennes langues vers les nouvelles
    if (savedLang && !codes.includes(savedLang)) {
      if (savedLang.startsWith('ch-')) {
        savedLang = savedLang === 'ch-de' ? 'de' : 'fr';
      } else {
        savedLang = null;
      }
    }

    const browserLang = this.translate.getBrowserLang();
    const detectedLang = browserLang && codes.includes(browserLang) ? browserLang : 'en';
    
    this.setLanguage(savedLang || detectedLang);
  }

  /**
   * Change la langue dynamiquement sans recharger la page
   */
  setLanguage(lang: string): void {
    const codes = this.languages.map(l => l.code);
    if (!codes.includes(lang)) {
      lang = 'en';
    }

    this.translate.use(lang);
    this.currentLanguageSubject.next(lang);
    localStorage.setItem('preferredLanguage', lang);
    
    // Mettre à jour l'attribut lang du document
    this.document.documentElement.setAttribute('lang', lang);
  }

  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  getCurrentLanguageObj(): Language {
    const code = this.getCurrentLanguage();
    return this.languages.find(l => l.code === code) || this.languages[0];
  }
}
